import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Constants } from 'src/app/app.constants';
import { MerchantService } from 'src/app/services/merchant.service';
import { Automation, Reward, StampCard, Voucher } from 'src/app/models';
import { RewardDialogComponent } from '../../card/reward-dialog/reward-dialog.component';
import { EmojiDialogComponent } from '../emoji-dialog/emoji-dialog.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { CardService } from 'src/app/services/card.service';
import { VoucherService } from 'src/app/services/voucher.service';
import { RewardService } from 'src/app/services/reward.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-automation-dialog',
  templateUrl: './automation-dialog.component.html',
  styleUrls: ['./automation-dialog.component.css'],
})
export class AutomationDialogComponent implements OnInit {
  automation: any = null; // Using 'any' to allow temporary properties like _tempRewardIds
  form!: FormGroup;
  saving = false;
  loading = false;

  nameFormControl!: FormControl;
  triggerTypeFormControl!: FormControl;
  messageFormControl!: FormControl;
  targetTypeFormControl!: FormControl;
  frequencyTypeFormControl!: FormControl;
  timeFormControl!: FormControl;
  dateFormControl!: FormControl;
  dayOfWeekFormControl!: FormControl;
  dayOfMonthFormControl!: FormControl;
  milestoneCardFormControl!: FormControl;
  milestoneThresholdFormControl!: FormControl;

  sendPushNotification = false;
  includeReward = false;
  rewards: any[] = [];

  filters: any = {
    birthdayThisMonth: false,
    receivedReward: false,
    specificRewardNotReceived: false,
    redeemedReward: false,
    receivedStamp: false,
    receivedNotification: false,
    hasVoucher: false,
    unredeemedRewards: false,
    lapsedStamps: false,
    hasCard: false,
  };

  selectedCards: StampCard[] = [];
  selectedVouchers: Voucher[] = [];
  selectedVoucherStatus: string[] = [];

  selectedCardsFormControl!: FormControl;
  conditionCardsFormControl!: FormControl;
  selectedVouchersFormControl!: FormControl;
  selectedVoucherStatusFormControl!: FormControl;
  specificRewardFormControl!: FormControl;

  cards: StampCard[] = [];
  vouchers: Voucher[] = [];
  availableRewards: Reward[] = [];
  cardsLoading = false;
  vouchersLoading = false;
  rewardsLoading = false;

  filterOptions: any[] = [];
  voucherStatusOptions: any[] = [];
  merchantUsesVouchers = true;

  triggerTypes = [
    {
      value: 'customer_takes_loyalty_card',
      label: 'Customer takes loyalty card',
    },
  ];

  targetTypes = [
    { value: 'all_customers', label: 'All customers' },
    { value: 'filtered_customers', label: 'Filtered customers' },
  ];

  frequencyTypes = [
    { value: 'once', label: 'Once (specific date)' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'milestone', label: 'Milestone (stamp count)' },
  ];

  daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  times: string[] = [
    '00:00',
    '01:00',
    '02:00',
    '03:00',
    '04:00',
    '05:00',
    '06:00',
    '07:00',
    '08:00',
    '09:00',
    '10:00',
    '11:00',
    '12:00',
    '13:00',
    '14:00',
    '15:00',
    '16:00',
    '17:00',
    '18:00',
    '19:00',
    '20:00',
    '21:00',
    '22:00',
    '23:00',
  ];

  contentMaxChar = 180;
  cursorPosition = 0;

  constructor(
    public matDialogRef: MatDialogRef<AutomationDialogComponent>,
    public fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    public constants: Constants,
    private merchantService: MerchantService,
    private cardService: CardService,
    private voucherService: VoucherService,
    private rewardService: RewardService,
    private authService: AuthService,
  ) {
    this.automation = data?.automation || null;
  }

  ngOnInit(): void {
    if (
      this.automation &&
      (!this.automation.trigger?.type ||
        this.automation.trigger.type === 'schedule')
    ) {
      this.constants.snack(
        'Scheduled automations are temporarily unavailable in the editor',
      );
      this.matDialogRef.close();
      return;
    }

    this.initializeFilterOptions();
    this.initializeForm();
    this.loadData();

    if (this.automation) {
      this.populateForm();
    }
  }

  initializeFilterOptions(): void {
    this.merchantUsesVouchers =
      this.authService.getNestedUserProperty('merchant', 'vouchers') === true;
    if (!this.merchantUsesVouchers) {
      this.filters['hasVoucher'] = false;
    }
    this.filterOptions = [
      {
        label: 'Upcoming birthday this month',
        key: 'birthdayThisMonth',
      },
      { label: 'Has never received a reward', key: 'receivedReward' },
      {
        label: 'Has not received a specific reward in x+ months',
        key: 'specificRewardNotReceived',
        followOn: {
          label: 'Number of months',
          key: 'specificRewardNotReceivedMonths',
          formControl: new FormControl(),
        },
      },
      { label: 'Has never redeemed a reward', key: 'redeemedReward' },
      {
        label: 'Has never received a stamp or points',
        key: 'receivedStamp',
      },
      {
        label: 'Has never received a push notification',
        key: 'receivedNotification',
      },
      {
        label: 'Has un-redeemed rewards',
        key: 'unredeemedRewards',
      },
      {
        label: 'Has not received a stamp or points in x+ days',
        key: 'lapsedStamps',
        followOn: {
          label: 'Number of days',
          key: 'lapsedStampsDays',
          formControl: new FormControl(),
        },
      },
      {
        label: 'Has at least one voucher',
        key: 'hasVoucher',
      },
    ].filter(
      (option) => option.key !== 'hasVoucher' || this.merchantUsesVouchers,
    );

    this.voucherStatusOptions = [
      {
        label: 'Active',
        value: 'active',
        description: 'Voucher is valid and not yet redeemed',
      },
      {
        label: 'Pending',
        value: 'pending',
        description: 'Voucher has not yet become active',
      },
      {
        label: 'Expired',
        value: 'expired',
        description: 'Voucher has expired',
      },
      {
        label: 'Redeemed',
        value: 'redeemed',
        description: 'Voucher has been fully redeemed',
      },
      {
        label: 'Cancelled',
        value: 'cancelled',
        description: 'Voucher has been cancelled',
      },
      {
        label: 'Partial',
        value: 'partial',
        description: 'Voucher has been partially redeemed',
      },
    ];
  }

  initializeForm(): void {
    this.nameFormControl = new FormControl('', Validators.required);
    this.triggerTypeFormControl = new FormControl(
      this.triggerTypes[0],
      Validators.required,
    );
    this.messageFormControl = new FormControl(''); // No longer required by default
    this.targetTypeFormControl = new FormControl(
      this.targetTypes[0],
      Validators.required,
    );
    this.frequencyTypeFormControl = new FormControl(
      this.frequencyTypes[3],
      Validators.required,
    );
    this.timeFormControl = new FormControl('09:00', Validators.required);
    this.dateFormControl = new FormControl('');
    this.dayOfWeekFormControl = new FormControl(this.daysOfWeek[1]);
    this.dayOfMonthFormControl = new FormControl(1);
    this.milestoneCardFormControl = new FormControl(null);
    this.milestoneThresholdFormControl = new FormControl(null);

    this.selectedCardsFormControl = new FormControl([]);
    this.conditionCardsFormControl = new FormControl([]);
    this.selectedVouchersFormControl = new FormControl([]);
    this.selectedVoucherStatusFormControl = new FormControl([]);
    this.specificRewardFormControl = new FormControl(null);

    this.form = this.fb.group({
      name: this.nameFormControl,
      triggerType: this.triggerTypeFormControl,
      message: this.messageFormControl,
      targetType: this.targetTypeFormControl,
      frequencyType: this.frequencyTypeFormControl,
      time: this.timeFormControl,
      date: this.dateFormControl,
      dayOfWeek: this.dayOfWeekFormControl,
      dayOfMonth: this.dayOfMonthFormControl,
      milestoneCard: this.milestoneCardFormControl,
      milestoneThreshold: this.milestoneThresholdFormControl,
      selectedCards: this.selectedCardsFormControl,
      conditionCards: this.conditionCardsFormControl,
      selectedVouchers: this.selectedVouchersFormControl,
      selectedVoucherStatus: this.selectedVoucherStatusFormControl,
      specificReward: this.specificRewardFormControl,
    });
  }

  populateForm(): void {
    if (!this.automation) return;

    this.nameFormControl.setValue(this.automation.name);
    this.messageFormControl.setValue(this.automation.action.message);

    const triggerType = this.triggerTypes.find(
      (t) =>
        t.value ===
        (this.automation.trigger?.type || 'customer_takes_loyalty_card'),
    );
    this.triggerTypeFormControl.setValue(triggerType || this.triggerTypes[0]);

    // Set target type
    const targetType = this.targetTypes.find(
      (t) => t.value === (this.automation!.target?.type || 'all_customers'),
    );
    this.targetTypeFormControl.setValue(targetType || this.targetTypes[0]);

    // Set frequency type
    const freqType = this.frequencyTypes.find(
      (f) => f.value === (this.automation!.frequency?.type || 'monthly'),
    );
    this.frequencyTypeFormControl.setValue(freqType || this.frequencyTypes[3]);

    this.timeFormControl.setValue(this.automation.frequency?.time || '09:00');

    // Set frequency-specific fields
    if (
      this.automation.frequency?.type === 'once' &&
      this.automation.frequency?.date
    ) {
      this.dateFormControl.setValue(this.automation.frequency.date);
    }
    if (
      this.automation.frequency?.type === 'weekly' &&
      this.automation.frequency?.dayOfWeek !== undefined
    ) {
      const dayOfWeek = this.daysOfWeek.find(
        (d) => d.value === this.automation!.frequency.dayOfWeek,
      );
      this.dayOfWeekFormControl.setValue(
        dayOfWeek || this.automation.frequency.dayOfWeek,
      );
    }
    if (
      this.automation.frequency?.type === 'monthly' &&
      this.automation.frequency?.dayOfMonth
    ) {
      this.dayOfMonthFormControl.setValue(this.automation.frequency.dayOfMonth);
    }
    if (
      this.automation.frequency?.type === 'milestone' &&
      this.automation.frequency?.milestoneCard
    ) {
      // Will be set after cards are loaded
      setTimeout(() => {
        const card = this.cards.find(
          (c) =>
            c._id === (this.automation!.frequency.milestoneCard as any)?._id ||
            this.automation!.frequency.milestoneCard,
        );
        if (card) {
          this.milestoneCardFormControl.setValue(card);
        }
      }, 500);
    }
    if (
      this.automation.frequency?.type === 'milestone' &&
      this.automation.frequency?.milestoneThreshold
    ) {
      this.milestoneThresholdFormControl.setValue(
        this.automation.frequency.milestoneThreshold,
      );
    }

    // Check if message exists to determine if push notification is enabled
    if (this.automation.action.message) {
      this.sendPushNotification = true;
    } else {
      this.sendPushNotification = false;
    }

    // Support both legacy single reward and new multiple rewards
    // Note: rewards will be populated after getAvailableRewards() completes
    if (this.automation.action.rewards?.length) {
      this.includeReward = true;
      // Store the reward IDs temporarily - will be resolved after rewards are loaded
      this.automation._tempRewardIds = this.automation.action.rewards.map(
        (r: any) => (typeof r === 'string' ? r : r._id),
      );
    } else if (this.automation.action.reward) {
      this.includeReward = true;
      this.automation._tempRewardIds = [
        typeof this.automation.action.reward === 'string'
          ? this.automation.action.reward
          : this.automation.action.reward._id,
      ];
    }

    if (this.automation.target?.filters) {
      this.filters = { ...this.automation.target.filters };
      if (!this.merchantUsesVouchers) {
        this.filters['hasVoucher'] = false;
      }

      // Populate follow-on form controls
      this.filterOptions.forEach((option) => {
        if (option.followOn && this.filters[option.followOn.key]) {
          option.followOn.formControl.setValue(
            this.filters[option.followOn.key],
          );
        }
      });
    }

    // Populate cards, vouchers, and voucher status after they're loaded
    // This will be done in the getCards and getVouchers methods
    if (this.automation.target?.cards?.length) {
      setTimeout(() => {
        // Handle both populated objects and IDs
        const cardIds = this.automation!.target.cards!.map((c: any) =>
          typeof c === 'string' ? c : c._id,
        );
        this.selectedCardsFormControl.setValue(
          this.cards.filter((c) => c._id && cardIds.includes(c._id)),
        );
      }, 100);
    }

    // Store voucher IDs temporarily - will be resolved after vouchers are loaded
    if (this.automation.target?.vouchers?.length) {
      this.automation._tempVoucherIds = this.automation.target.vouchers.map(
        (v: any) => (typeof v === 'string' ? v : v._id),
      );
    }

    // Store voucher status temporarily - will be set after vouchers are loaded
    if (this.automation.target?.voucherStatus?.length) {
      this.automation._tempVoucherStatus = this.automation.target.voucherStatus;
    }

    if (this.automation.conditions?.cards?.length) {
      this.automation._tempConditionCardIds =
        this.automation.conditions.cards.map((c: any) =>
          typeof c === 'string' ? c : c._id,
        );
      setTimeout(() => {
        if (
          this.automation?._tempConditionCardIds?.length &&
          this.cards.length
        ) {
          this.conditionCardsFormControl.setValue(
            this.cards.filter(
              (c) =>
                c._id &&
                this.automation!._tempConditionCardIds!.includes(c._id),
            ),
          );
          delete this.automation._tempConditionCardIds;
        }
      }, 100);
    }
  }

  loadData(): void {
    this.getCards();
    this.getVouchers();
    this.getAvailableRewards();
  }

  getCards(): void {
    this.cardsLoading = true;
    this.cardService
      .getCards(100, 0)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.cards = res.docs;

            const titleCounts = this.cards.reduce(
              (acc, card) => {
                if (card.title) {
                  acc[card.title] = (acc[card.title] || 0) + 1;
                }
                return acc;
              },
              {} as Record<string, number>,
            );

            this.cards.forEach((el) => {
              if (el.title && titleCounts[el.title] > 1) {
                el.title = `${el.title} (#${el.ref})`;
              }
            });

            // Initialize with all cards selected
            this.selectedCardsFormControl.setValue(this.cards);
            this.conditionCardsFormControl.setValue(this.cards);

            if (this.automation?._tempConditionCardIds?.length) {
              this.conditionCardsFormControl.setValue(
                this.cards.filter(
                  (c) =>
                    c._id &&
                    this.automation!._tempConditionCardIds!.includes(c._id),
                ),
              );
              delete this.automation._tempConditionCardIds;
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.cardsLoading = false));
  }

  getVouchers(): void {
    this.vouchersLoading = true;
    this.voucherService
      .getVouchers(100, 0, true)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.vouchers = res.docs.map((el: Voucher) => {
              return {
                ...el,
                displayTitle: el.displayAs,
              };
            });

            // Populate selected vouchers from automation if editing
            if (this.automation?._tempVoucherIds?.length) {
              this.selectedVouchersFormControl.setValue(
                this.vouchers.filter(
                  (v) =>
                    v._id && this.automation!._tempVoucherIds!.includes(v._id),
                ),
              );
              delete this.automation._tempVoucherIds;
            }

            // Populate voucher status from automation if editing
            if (this.automation?._tempVoucherStatus?.length) {
              this.selectedVoucherStatusFormControl.setValue(
                this.voucherStatusOptions.filter((s) =>
                  this.automation!._tempVoucherStatus!.includes(s.value),
                ),
              );
              delete this.automation._tempVoucherStatus;
            } else {
              this.selectedVoucherStatusFormControl.setValue(
                this.voucherStatusOptions.filter((el) =>
                  ['active', 'partial'].includes(el.value),
                ),
              );
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.vouchersLoading = false));
  }

  getAvailableRewards(): void {
    this.rewardsLoading = true;
    this.rewardService
      .getRewards(250, 0)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.availableRewards = res.docs ?? [];
            const selectedRewardId =
              this.filters['specificRewardNotReceivedReward'];
            if (selectedRewardId) {
              const selectedReward = this.availableRewards.find(
                (reward) =>
                  reward._id?.toString() === selectedRewardId?.toString(),
              );
              this.specificRewardFormControl.setValue(selectedReward || null);
            }

            // Populate rewards from automation if editing
            if (this.automation?._tempRewardIds?.length) {
              this.rewards = this.automation._tempRewardIds
                .map((rewardId: string) => {
                  const reward = this.availableRewards.find(
                    (r) => r._id?.toString() === rewardId?.toString(),
                  );
                  return reward ? { reward } : null;
                })
                .filter((r: any) => r !== null);
              delete this.automation._tempRewardIds;
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.rewardsLoading = false));
  }

  toggleFilter(key: string, value: any): void {
    this.filters[key] = value;

    if (key === 'specificRewardNotReceived' && !value) {
      this.filters['specificRewardNotReceivedMonths'] = undefined;
      this.filters['specificRewardNotReceivedReward'] = undefined;
      this.filterOptions.forEach((option) => {
        if (option.key === 'specificRewardNotReceived' && option.followOn) {
          option.followOn.formControl.setValue(null);
        }
      });
      this.specificRewardFormControl.setValue(null);
    }
  }

  openRewardDialog(): void {
    const dialogRef = this.dialog.open(RewardDialogComponent, {
      data: {
        rewardOnly: true,
        exclusions: this.rewards,
      },
      width: '450px',
      autoFocus: false,
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe((data: any) => {
      if (data) {
        this.rewards.push(data);
      }
    });
  }

  removeReward(item: any): void {
    const index = this.rewards.findIndex((el) => el === item);
    if (index !== -1) {
      this.rewards.splice(index, 1);
    }
  }

  toggleReward(): void {
    this.includeReward = !this.includeReward;
    if (!this.includeReward) {
      this.rewards = [];
    }
  }

  togglePushNotification(): void {
    this.sendPushNotification = !this.sendPushNotification;
    if (!this.sendPushNotification) {
      this.messageFormControl.setValue('');
    }
  }

  save(): void {
    if (!this.form.valid) {
      this.constants.snack('Please complete all required fields');
      return;
    }

    // Validate that at least one action is specified
    const hasMessage =
      this.sendPushNotification &&
      this.messageFormControl.value &&
      this.messageFormControl.value.trim().length > 0;
    const hasRewards = this.includeReward && this.rewards?.length > 0;

    if (!hasMessage && !hasRewards) {
      this.constants.snack(
        'Please either add a message or at least one reward',
      );
      return;
    }

    if (this.sendPushNotification && !this.messageFormControl.value?.trim()) {
      this.constants.snack('Please enter a message for the push notification');
      return;
    }

    if (this.includeReward && !this.rewards?.length) {
      this.constants.snack('Please include at least one reward');
      return;
    }

    if (this.isCardAcquisitionTrigger) {
      if (!this.conditionCardsFormControl.value?.length) {
        this.constants.snack('Please select at least one loyalty card');
        return;
      }
    }

    if (this.isScheduleTrigger) {
      for (const filterOption of this.filterOptions) {
        if (filterOption.followOn) {
          const mainFilter = this.filters[filterOption.key];
          const followOnFilter = this.filters[filterOption.followOn.key];
          if (mainFilter && !followOnFilter) {
            this.constants.snack('Please complete all fields');
            return;
          }
        }
      }

      if (
        this.filters['specificRewardNotReceived'] &&
        !this.specificRewardFormControl.value?._id
      ) {
        this.constants.snack('Please select a reward');
        return;
      }

      if (
        this.filters['hasCard'] &&
        !this.selectedCardsFormControl.value?.length
      ) {
        this.constants.snack('Please select at least one card');
        return;
      }

      if (this.filters['hasVoucher']) {
        if (!this.selectedVouchersFormControl.value?.length) {
          this.constants.snack('Please select at least one voucher');
          return;
        }
        if (!this.selectedVoucherStatusFormControl.value?.length) {
          this.constants.snack('Please select at least one voucher status');
          return;
        }
      }

      // Validate milestone fields
      const frequencyType =
        this.frequencyTypeFormControl.value?.value ||
        this.frequencyTypeFormControl.value;
      if (frequencyType === 'milestone') {
        if (!this.milestoneCardFormControl.value) {
          this.constants.snack(
            'Please select a card for the milestone trigger',
          );
          return;
        }
        if (
          !this.milestoneThresholdFormControl.value ||
          this.milestoneThresholdFormControl.value < 1
        ) {
          this.constants.snack(
            'Please enter a valid number of stamps for the milestone',
          );
          return;
        }
      }
    }

    const triggerType =
      this.triggerTypeFormControl.value?.value ||
      this.triggerTypeFormControl.value;
    const frequencyType =
      this.frequencyTypeFormControl.value?.value ||
      this.frequencyTypeFormControl.value;

    const data: any = {
      name: this.nameFormControl.value,
      enabled: true,
      trigger: {
        type: triggerType,
      },
      conditions: this.isCardAcquisitionTrigger
        ? {
            cards:
              this.conditionCardsFormControl.value?.map(
                (c: StampCard) => c._id,
              ) || [],
          }
        : undefined,
      action: {
        type: hasMessage ? 'push_notification' : 'give_rewards',
        message: this.sendPushNotification
          ? this.messageFormControl.value
          : undefined,
        rewards:
          this.includeReward && this.rewards?.length
            ? this.rewards.map((r) => r.reward._id)
            : undefined,
      },
      target: this.isScheduleTrigger
        ? {
            type:
              this.targetTypeFormControl.value?.value ||
              this.targetTypeFormControl.value,
            filters: this.isFiltered
              ? {
                  ...this.filters,
                  specificRewardNotReceivedReward:
                    this.specificRewardFormControl.value?._id,
                }
              : undefined,
            cards: this.filters['hasCard']
              ? this.selectedCardsFormControl.value?.map(
                  (c: StampCard) => c._id,
                ) || []
              : [],
            vouchers:
              this.selectedVouchersFormControl.value?.map(
                (v: Voucher) => v._id,
              ) || [],
            voucherStatus:
              this.selectedVoucherStatusFormControl.value?.map(
                (s: any) => s.value,
              ) || [],
          }
        : undefined,
      frequency: this.isScheduleTrigger
        ? {
            type: frequencyType,
            time: this.timeFormControl.value,
            date:
              frequencyType === 'once' ? this.dateFormControl.value : undefined,
            dayOfWeek:
              frequencyType === 'weekly'
                ? (this.dayOfWeekFormControl.value?.value ??
                  this.dayOfWeekFormControl.value)
                : undefined,
            dayOfMonth:
              frequencyType === 'monthly'
                ? this.dayOfMonthFormControl.value
                : undefined,
            milestoneType:
              frequencyType === 'milestone' ? 'stamp_count' : undefined,
            milestoneCard:
              frequencyType === 'milestone'
                ? this.milestoneCardFormControl.value?._id
                : undefined,
            milestoneThreshold:
              frequencyType === 'milestone'
                ? this.milestoneThresholdFormControl.value
                : undefined,
          }
        : undefined,
    };

    this.saving = true;

    const request = this.automation
      ? this.merchantService.updateAutomation(this.automation._id!, data)
      : this.merchantService.createAutomation(data);

    request
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.constants.snack(
              this.automation ? 'Automation updated' : 'Automation created',
            );
            this.matDialogRef.close(res);
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  openEmojiPicker(): void {
    const dialogRef = this.dialog.open(EmojiDialogComponent, {
      autoFocus: false,
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data?.emoji) {
        this.insertEmojiAtCursor(data.emoji);
      }
    });
  }

  insertEmojiAtCursor(emoji: string): void {
    const currentValue = this.messageFormControl.value || '';
    const newValue =
      currentValue.slice(0, this.cursorPosition) +
      emoji +
      currentValue.slice(this.cursorPosition);
    this.messageFormControl.setValue(newValue);
  }

  insertCustomerNameAtCursor(): void {
    const currentValue = this.messageFormControl.value || '';
    const newValue =
      currentValue.slice(0, this.cursorPosition) +
      '[customer_name]' +
      currentValue.slice(this.cursorPosition);
    this.messageFormControl.setValue(newValue);
  }

  updateCursorPosition(event: any): void {
    this.cursorPosition = event.target.selectionStart;
  }

  close(): void {
    this.matDialogRef.close();
  }

  get isFiltered(): boolean {
    if (!this.isScheduleTrigger) {
      return false;
    }
    const targetType =
      this.targetTypeFormControl.value?.value ||
      this.targetTypeFormControl.value;
    return targetType === 'filtered_customers';
  }

  get selectedTriggerType(): string {
    return (
      this.triggerTypeFormControl.value?.value ||
      this.triggerTypeFormControl.value
    );
  }

  get isScheduleTrigger(): boolean {
    return this.selectedTriggerType === 'schedule';
  }

  get isCardAcquisitionTrigger(): boolean {
    return this.selectedTriggerType === 'customer_takes_loyalty_card';
  }

  get showDatePicker(): boolean {
    if (!this.isScheduleTrigger) {
      return false;
    }
    const freqType =
      this.frequencyTypeFormControl.value?.value ||
      this.frequencyTypeFormControl.value;
    return freqType === 'once';
  }

  get showDayOfWeek(): boolean {
    if (!this.isScheduleTrigger) {
      return false;
    }
    const freqType =
      this.frequencyTypeFormControl.value?.value ||
      this.frequencyTypeFormControl.value;
    return freqType === 'weekly';
  }

  get showDayOfMonth(): boolean {
    if (!this.isScheduleTrigger) {
      return false;
    }
    const freqType =
      this.frequencyTypeFormControl.value?.value ||
      this.frequencyTypeFormControl.value;
    return freqType === 'monthly';
  }

  get showMilestoneFields(): boolean {
    if (!this.isScheduleTrigger) {
      return false;
    }
    const freqType =
      this.frequencyTypeFormControl.value?.value ||
      this.frequencyTypeFormControl.value;
    return freqType === 'milestone';
  }

  get loadingData(): boolean {
    return this.cardsLoading || this.vouchersLoading || this.rewardsLoading;
  }
}
